import React, { useState, useContext, useCallback } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { eventDonationFieldInteraction, eventFormFieldInteraction, PROTECT_FIELD_VALUE } from "../../utilities/dataLayer.js";

import { Global } from "../../context/globalContextProvider.js";
import RichTextComponent from "../contentfulModal/richTextComponent.js";
import TextInput from "../inputs/TextInput.js";

const SubscriptionWidgetForm = ({ widgetData }) => {
  const { mailingList, mobilePhoneMandatory, buttonText, buttonType, privacyText, successRedirect } = widgetData;

  const {
    stickyHoney,
    setStickyHoney,
    isFormValid,
    // setIsFormValid,
    formElement,
    validateForm,
    formPost,
  } = useContext(Global);

  //recaptcha related functions
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      return;
    }
    // Do whatever you want with the token
    return await executeRecaptcha("subscription");
  }, [executeRecaptcha]);

  //set formSubmitSuccess by url param. e.g. page-url?success=1
  const isBrowser = () => typeof window !== "undefined"; //gatsby doesn't like the window object when build, but it's fine after building it
  let formSubmitSuccess;
  if (isBrowser()) {
    const urlParams = new URLSearchParams(window.location.search);
    formSubmitSuccess = urlParams.get("success");
  }

  const [isFreshSubmit, setIsFreshSubmit] = useState(true);
  const [formError, setFormError] = useState(null);

  const handleVerifyAndSubmit = async (event) => {
    event.preventDefault();
    const token = await handleReCaptchaVerify();
    if (!token) return;
    handleSubmit(token);
  };

  const handleClick = (event) => {
    const target = event.target;
    if (target.tagName.toLowerCase() === "button" && target.parentNode.hasAttribute("selectorgroup")) {
      eventDonationFieldInteraction(event);
    }
  };

  const handleFieldBlur = (event, name, isTextInput = false) => {
    let fieldValue;
    if (isTextInput) {
      fieldValue = PROTECT_FIELD_VALUE;
    } else if (typeof event?.target.checked !== "undefined") {
      fieldValue = event.target.checked ? "checked" : "unchecked";
    } else {
      fieldValue = event?.target.value;
    }
    eventFormFieldInteraction(name, "subscription form", name, fieldValue);
  };

  const handleSubmit = (token) => {
    if (!isFreshSubmit) return;
    setIsFreshSubmit(false);

    fetch(`${process.env.GATSBY_FORM_ENDPOINT}/subscription/post`, {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        first_name: formPost.first_name,
        last_name: formPost.last_name,
        email: formPost.email,
        mailing_list: mailingList,
        phone: formPost.phone,
        sticky_honey: stickyHoney,
        "g-recaptcha-response": token,
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (successRedirect) {
          window.location.href = successRedirect;
        } else {
          window.location.search = `&success=1`;
          window.location.hash = mailingList;
        }
      })
      .catch(function (error) {
        setIsFreshSubmit(true);
        setFormError("There is an error for your submission, please try again later.");
        console.log("There has been a problem with your submission: " + error.message);
      });
  };
  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
  return (
    <form
      id={mailingList}
      ref={formElement}
      onKeyUp={validateForm}
      onSubmit={handleVerifyAndSubmit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
      className={`contactForm contactForm--subscription-widget`}
    >
      <input
        value={stickyHoney ?? ""}
        type="text"
        name="a_password"
        style={{ display: "none" }}
        tabIndex="-1"
        autoComplete="off"
        onChange={(e) => setStickyHoney(e.target.value)}
      />
      <div className="form-group donation-form" onClick={handleClick}>
        <div className="wrapper">
          <div className="body">
            <TextInput
              name="First name"
              type="text"
              required={true}
              disabled={false}
              key={"first-name"}
              validation={[]}
              validationShow={true}
              msgValidation={"Please enter your first name"}
              showValidaiton={false}
              uniqueId={"first-name"}
              options={null}
              onBlurChanged={handleFieldBlur}
            />

            <TextInput
              name="Last name"
              type="text"
              required={true}
              disabled={false}
              key="last-name"
              validation={[]}
              validationShow={true}
              msgValidation="Please enter your last name"
              showValidaiton={false}
              uniqueId="last-name"
              options={null}
              onBlurChanged={handleFieldBlur}
            />

            <TextInput
              name={"Email"}
              type="email"
              required={true}
              disabled={false}
              key="email"
              validation={null}
              validationShow={false}
              msgValidation="Please enter a valid email address"
              showValidaiton={true}
              uniqueId="email"
              onBlurChanged={handleFieldBlur}
            />

            <TextInput
              name={"Phone"}
              type="text"
              required={mobilePhoneMandatory}
              disabled={false}
              key="phone-number"
              validation={null}
              validationShow={false}
              msgValidation="Please enter a valid phone number"
              showValidaiton={false}
              uniqueId="phone-number"
              onBlurChanged={handleFieldBlur}
            />
          </div>
        </div>
      </div>
      {formSubmitSuccess === "1" ? (
        <div className="rte">
          <div className="rte--container">
            <div className="rte--container__single">
              <p>Thank you for your submission. </p>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
      {formError ? <p className="form-error">{formError}</p> : ""}
      <button
        type="submit"
        className={isFormValid ? `btn btn-${buttonType}` : "btn btn-disabled"}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleVerifyAndSubmit();
          }
        }}
      >
        {isFreshSubmit ? buttonText : "Processing..."}
      </button>

      {privacyText && (
        <div className="subscription-widget__privacy-text">
          <RichTextComponent data={privacyText} />
        </div>
      )}
    </form>
  );
  /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
};

const WrapWithGoogleRecaptcha = ({ widgetData }) => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={`${process.env.GATSBY_RECAPTCHA_PUBLIC_SITE_KEY_V3}`}>
      <SubscriptionWidgetForm widgetData={widgetData} />
    </GoogleReCaptchaProvider>
  );
};

export default WrapWithGoogleRecaptcha;
